import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import searchIcon from "../../assests/svg/searchIcon.svg";

import { IoEyeOutline } from "react-icons/io5";
import { RxCross1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  OrganizationIcon,
  QrIcon,
  ServiceScoreIcon,
  TipAmountIcon,
  TipNumberIcon,
  TipRevenueIcon,
  TransactionIcon,
  propertyDashIcon,
} from "../../assests/Icons/dashboardIcons";
import {
  clearUserLocalData,
  DEFAULT_TIPPING_VIEW_SIMPLE,
  getCurrentUserData,
  isDefaultTippingViewDisabled,
  notifyBugsnagError,
  showToast,
} from "../../common/utils/Functions";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import PageHeading from "../../components/pageHeading";
import ReactTable from "../../components/reactTable/ReactTable";
import InputField from "../../components/theme/InputField";
import ThemeButton from "../../components/theme/ThemeButton";
import useFetch from "../../hooks/useFetch";
import { WIZARD_TRACK_CLICK_TYPE } from "../../types/wizardTrackClickType";
import { AppDispatch, RootState } from "../../redux/store";
import { setPropertiesCount } from "../../redux/common";
import styles from "./userDashboard.module.scss";
import RatingsPromptModal from "../../components/modals/tippingModal/RatingsPromptModal";
import TippingModal from "../../components/modals/tippingModal/TippingModal";

const Dashboard = () => {
  const { uuid } = useParams();
  const LocalData = getCurrentUserData(`${uuid}`);
  const [showTippingModal, setShowTippingModal] = useState(false);
  const [showRatingsPrompt, setShowRatingsPrompt] = useState(false);
  const [propertyCount, setPropertyCount] = useState<number | null>(() => {
    const stored = localStorage.getItem("properties_count");
    return stored === null ? null : Number(stored);
  });
  const [trackClicksApi] = useFetch("/common/track-clicks", { method: "put" });
  const [enableSimpleTippingApi, { loading: enableSimpleTippingLoading }] =
    useFetch(`/organization/customize/${LocalData?.uuid}`, { method: "put" });
  const [
    viewOrganizationApi,
    { response: viewOrganization, error: organizationError },
  ] = useFetch(`/organization/view/${LocalData?.uuid}`);

  useEffect(() => {
    const ratingsFlag = localStorage.getItem("showRatingsPrompt") === "true";
    const tippingFlag = localStorage.getItem("showTippingModal") === "true";
    if (!ratingsFlag && !tippingFlag) return;
    if (LocalData?.uuid && tippingFlag) {
      viewOrganizationApi();
    }
  }, []);

  useEffect(() => {
    const ratingsFlag = localStorage.getItem("showRatingsPrompt") === "true";
    const tippingFlag = localStorage.getItem("showTippingModal") === "true";
    if (!ratingsFlag && !tippingFlag) return;
    if (propertyCount === null) return;

    if (propertyCount > 0) {
      localStorage.setItem("showRatingsPrompt", "true");
      localStorage.removeItem("showTippingModal");
      setShowTippingModal(false);
      setShowRatingsPrompt(true);
      return;
    }

    if (ratingsFlag) {
      const storedTips = localStorage.getItem("total_tips");
      const leavesForQr =
        LocalData?.role_id === 2 &&
        storedTips !== null &&
        Number(storedTips) === 0;
      if (!leavesForQr) {
        setShowRatingsPrompt(true);
      }
      return;
    }

    if (!viewOrganization && !organizationError) return;

    if (isDefaultTippingViewDisabled(viewOrganization?.data?.tipping_view)) {
      localStorage.removeItem("showTippingModal");
      localStorage.setItem("showRatingsPrompt", "true");
      setShowTippingModal(false);
      setShowRatingsPrompt(true);
      return;
    }

    localStorage.removeItem("showRatingsPrompt");
    setShowRatingsPrompt(false);
    setShowTippingModal(true);
  }, [propertyCount, viewOrganization, organizationError]);

  // Guard: redirect org users away from dashboard if no tips recorded yet.
  // Owners who already have properties stay on the dashboard.
  useEffect(() => {
    if (LocalData?.role_id !== 2) return;
    if (Number(localStorage.getItem("properties_count") ?? "0") > 0) return;
    const stored = localStorage.getItem("total_tips");
    if (stored !== null && Number(stored) === 0) {
      navigate(uuid ? `/${uuid}/QR-Codes-&-links` : "/QR-Codes-&-links");
    }
  }, []);

  const closeTippingModal = () => {
    setShowTippingModal(false);
    setShowRatingsPrompt(false);
    localStorage.removeItem("showTippingModal");
    localStorage.removeItem("showRatingsPrompt");
  };

  const startTippingJourney = () => {
    trackClicksApi({ type: WIZARD_TRACK_CLICK_TYPE.CLICKED_WIZARD_SPLASH }).catch(() => {});
    closeTippingModal();
    navigate("/tipping-experience");
  };

  const addTipping = () => {
    const hasProperties =
      (propertyCount ?? Number(localStorage.getItem("properties_count") ?? "0")) > 0;
    enableSimpleTippingApi({ tipping_view: DEFAULT_TIPPING_VIEW_SIMPLE })
      .then(() => {
        if (hasProperties) {
          closeTippingModal();
          return;
        }
        startTippingJourney();
      })
      .catch((e: any) => {
        showToast(e?.message as string, "error");
        notifyBugsnagError(e, { api: "enableSimpleTippingApi" });
      });
  };
  const initialCountData =
    LocalData?.role_id === 3
      ? [
        {
          icon: propertyDashIcon,
          title: "Manage Properties",
          key: "manageProperties",
          link: uuid
            ? "/" + uuid + "/manage-properties"
            : "/manage-properties",
        },
        {
          icon: QrIcon,
          title: "QR Codes & Links",
          key: "qrCodes",
          link: uuid ? "/" + uuid + "/QR-Codes-&-links" : "/QR-Codes-&-links",
        },
      ]
      : [
        {
          icon: TransactionIcon,
          title: "Tip Transactions Today",
          amount: 0,
          key: "todayTipTransaction",
          onclick: () => {
            navigate(uuid ? "/" + uuid + "/reporting" : "/reporting", {
              state: {
                filterValue: { value: "today", label: "Today" },
              },
            });
          },
        },
        {
          icon: OrganizationIcon,
          title: "Properties",
          amount: 0,
          key: "properties",
          onclick: () => {
            navigate(
              uuid ? "/" + uuid + "/manage-properties" : "/manage-properties"
            );
          },
        },
        {
          icon: ServiceScoreIcon,
          title: "Service Score",
          amount: 0,
          key: "star",
          onclick: () => {
            navigate(uuid ? "/" + uuid + "/reporting" : "/reporting");
          },
        },
        {
          icon: TipNumberIcon,
          title: "Total Number of Tips",
          amount: 0,
          key: "totalTips",
          onclick: () => {
            navigate(uuid ? "/" + uuid + "/reporting" : "/reporting");
          },
        },
        {
          icon: TipAmountIcon,
          title: "Net Tip Amount",
          amount: 0,
          key: "totalTipperAmt",
          onclick: () => {
            navigate(uuid ? "/" + uuid + "/reporting" : "/reporting");
          },
        },
        {
          icon: TipRevenueIcon,
          title: "Average Tip Amount",
          amount: 0,
          key: "average_tip_amount",
          onclick: () => {
            navigate(uuid ? "/" + uuid + "/reporting" : "/reporting");
          },
        },
      ];
  // const initialCountData = [
  //   {
  //     icon: TransactionIcon,
  //     title: "Tip Transactions Today",
  //     amount: 0,
  //     key: "todayTipTransaction",
  //   },
  //   {
  //     icon: OrganizationIcon,
  //     title: "Properties",
  //     amount: 0,
  //     key: "properties",
  //   },
  //   {
  //     icon: TipperIcon,
  //     title: "Tippers",
  //     amount: 0,
  //     key: "tippers",
  //   },
  //   {
  //     icon: TipNumberIcon,
  //     title: "Total Number of Tips",
  //     amount: 0,
  //     key: "totalTips",
  //   },
  //   {
  //     icon: TipAmountIcon,
  //     title: "Net Tip Amount",
  //     amount: 0,
  //     key: "totalTipperAmt",
  //   },
  //   {
  //     icon: TipRevenueIcon,
  //     title: "Avergae Tip Amount",
  //     amount: 0,
  //     key: "average_tip_amount",
  //   },
  // ];
  const [dashbaordData, setDashboardData] = useState();

  const [searchValue, setSearchValue] = useState("");
  /**
   * @Pagination states
   * 1. pageLimit    :- store limit of data that will render on current page
   * 2. totalRecords :- store total number of data in partners api
   * 3. currentPage  :- store current page number
   */
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { data } = useSelector((store: RootState) => store.userData);

  /** Api definition */
  const [
    loginAsAdminApi,
    {
      response: loginAsAdminRes,
      loading: loginAsAdminLoading,
      error: loginAsAdminErr,
    },
  ] = useFetch(`/auth/admin-org-login?uuid=${data?.uuid}`);

  const urlForAdmin = (prop_name?: string) => {
    if (uuid) {
      return (
        "/" +
        uuid +
        `/manage-properties/view-property-tips/view-tip-transactions`
      );
    } else {
      return `/manage-properties/view-property-tips/view-tip-transactions`;
    }
  };
  /**
   * @Array :- for table headings that passed as a prop to React-Table component
   */
  const tableHeadings = [
    // {
    //   key: "propertyId",
    //   label: "Property  ID",
    //   onClick: (item: any) => {
    //     navigate(urlForAdmin(), {
    //       state: {
    //         uuid: item?.propertyUuid,
    //         name: item?.name,
    //       },
    //     });
    //   },
    // },
    {
      key: "name",
      label: "Property Name",
      onClick: (item: any) => {
        navigate(urlForAdmin(), {
          state: {
            uuid: item?.propertyUuid,
            name: item?.name,
          },
        });
      },
    },
    {
      key: "average_tipper_amt_count",
      label: "Average Tip Amount",
      textAlign: "right",
    },
    {
      key: "highest_tip_amount",
      label: "Highest Tip Amount",
      textAlign: "right",
    },
    {
      key: "netTipAmount",
      label: "Net Tip Amount",
     textAlign: "right",
    },
    {
      key: "total_number_of_tips",
      label: "Total Number of Tips",
      textAlign: "right",
    },
    {
      key: "average_rating",
      label: "Service Score",
      textAlign: "right",
    },
  ];

  const [countData, setCountData] = useState<any>(initialCountData);

  /**
   * @Array :- for action button dropdown values and passes to React table component
   */
  const actionButtonOptions =
    LocalData?.role_id === 3
      ? [
        {
          name: "View Property Info",
          icon: <IoEyeOutline />,
          onClick: (item: any) => {
            navigate(
              uuid
                ? "/" +
                uuid +
                `/manage-properties/view-property/${item.propertyUuid}`
                : `/manage-properties/view-property/${item.propertyUuid}`
            );
          },
        },
      ]
      : [
        {
          name: "View Property Info",
          icon: <IoEyeOutline />,
          onClick: (item: any) => {
            navigate(
              uuid
                ? "/" +
                uuid +
                `/manage-properties/view-property/${item.propertyUuid}`
                : `/manage-properties/view-property/${item.propertyUuid}`
            );
          },
        },
        {
          name: "View Tip Transactions",
          icon: <IoEyeOutline />,
          onClick: (item: any) => {
            navigate(urlForAdmin(), {
              state: {
                uuid: item?.propertyUuid,
                name: item?.name,
              },
            });
          },
        },
      ];

  /** Api definition */
  const [countApi, { response: countResponse, error: countError }] =
    useFetch("/dashboard/count");

  /** user dashboard data */
  // const [
  //   getDashBoardData,
  //   {
  //     response: dashBoardDataResponse,
  //     error: dashBoardDataError,
  //     loading: dashBoardDataLoading,
  //   },
  // ] = useFetch("/dashboard/all-tips", {
  //   method: "POST",
  // });

  const [
    getDashBoardData,
    {
      response: dashBoardDataResponse,
      error: dashBoardDataError,
      loading: dashBoardDataLoading,
    },
  ] = useFetch("/dashboard/property-tips", {
    method: "POST",
  });

  useEffect(() => {
    if (dashBoardDataResponse) {
      setTotalRecords(dashBoardDataResponse.data?.totalRecords);
      const updatedList = dashBoardDataResponse.data?.list
        ?.filter((item: any) => item?.property_details !== null)
        .map((item: any) => ({
          propertyId: item?.property_id,
          propertyUuid: item?.property_uuid,
          name: item?.property_name,
          netTipAmount: item?.total_net_tip_amount?.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          average_tipper_amt_count:
            item?.average_tipper_amt_count?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          highest_tip_amount: item?.highest_tip_amount?.toLocaleString(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            }
          ),
          total_number_of_tips: item?.total_number_of_tips,
          average_rating:
            item?.star != null
              ? Number(item.star).toFixed(1)
              : item?.average_rating != null
                ? Number(item.average_rating).toFixed(1)
                : "-",
        }));
      setDashboardData(updatedList);
    }
    if (dashBoardDataError) {
      showToast(dashBoardDataError.message as string, "error");
    }
  }, [dashBoardDataResponse, dashBoardDataError]);
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        getDashBoardData({
          start: (currentPage - 1) * pageLimit,
          limit: pageLimit,
          // startDate: null,
          // endDate: null,
          organization_uuid: null,
          // sortedBy: null,
          search: searchValue,
        });
      } catch (e: any) {
        notifyBugsnagError(e, {
          api: "getDashBoardData",
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue, currentPage, pageLimit]);

  /** Call countApi */
  useEffect(() => {
    countApi();
  }, []);

  /** Handle countApi response */
  useEffect(() => {
    if (countResponse) {
      const responseData = countResponse.data;
      const totalTips = responseData?.totalTips ?? 0;
      const properties = Number(responseData?.properties ?? 0);
      setPropertyCount(properties);
      // Persist properties count for sidebar visibility (no extra API there)
      localStorage.setItem('properties_count', String(properties));
      // Keep total_tips fresh so sidebar shows/hides Dashboard correctly
      localStorage.setItem('total_tips', String(totalTips));
      // Also update global store so sidebar updates immediately
      dispatch(setPropertiesCount(Number(responseData?.properties ?? 0)));

      // Redirect org users away from dashboard if they have no tips yet.
      // Owners who already have properties stay on the dashboard.
      if (LocalData?.role_id === 2 && totalTips === 0 && properties === 0) {
        navigate(uuid ? `/${uuid}/QR-Codes-&-links` : "/QR-Codes-&-links");
        return;
      }

      const updatedData = countData.map((item: any) => ({
        ...item,
        amount:
          item.key === "star"
            ? Number(
                responseData?.star ?? responseData?.average_rating ?? 0
              )
            : Math.round(responseData[item.key]),
        avg_amount: responseData?.average_tip_amount,
      }));

      setCountData(updatedData);
    }
    if (countError) {
      showToast(countError.message as string, "error");
    }
  }, [countResponse, countError]);

  const handleReturnBack = () => {
    loginAsAdminApi();
  };

  useEffect(() => {
    if (loginAsAdminRes) {
      window.location.replace(
        `${process.env.REACT_APP_BASE_URL_ADMIN}/organizations/view/${uuid}`
      );
      clearUserLocalData(`${uuid}`);
    }
    if (loginAsAdminErr) {
      showToast(loginAsAdminErr.message as string, "error");
    }
  }, [loginAsAdminRes, loginAsAdminErr]);

  return (
    <DashboardLayout>
      <div className={styles.dash_page}>
        <Container>
          <Row>
            {/* {countResponse?.data?.stripe_status === 0 ||
              countResponse?.data?.stripe_status === 4 ||
              countResponse?.data?.stripe_status === 5 ? (
              <SetUpSuccess status={countResponse?.data?.stripe_status} />
            ) : countResponse?.data?.stripe_status === 3 ? (
              <SetUpError />
            ) : (
              ""
            )} */}
            <Col lg={6}>
              <PageHeading
                heading="Dashboard"
                subHeading="Here is the information about your organization."
              />
            </Col>
            {(LocalData?.admin_uuid || data?.admin_uuid) && (
              <Col
                lg={6}
                className="d-flex justify-content-end align-items-center"
              >
                <ThemeButton
                  onClick={handleReturnBack}
                  loading={loginAsAdminLoading}
                >
                  Return to Your Account
                </ThemeButton>
              </Col>
            )}
          </Row>

          {LocalData?.role_id === 3 && (
            <div className={styles.cardsWrap}>
              {countData.map((item: any) => (
                <Link
                  to={item.link}
                  className={styles.card_outer}
                  style={{
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  <div key={item.key} className={styles.card}>
                    <div className={styles.icon}>{item.icon}</div>
                    <div className={styles.content}>
                      <div className={styles.title}>{item.title}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {LocalData?.role_id === 2 && (
            <div
              className={`${styles.cardsWrap3} ${Number((countResponse?.data?.properties ?? (localStorage.getItem('properties_count') || 0))) === 0 ? styles.cardsWrap3NoProperties : ''}`}
            >
              {countData
                .filter((item: any) => !(item?.key === "properties" && Number(item?.amount) === 0))
                .map((item: any) => (
                  <div
                    className={styles.card_outer}
                    key={item.key}
                    onClick={item.onclick}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`${styles.card} ${item.key === "average_tip_amount"
                        ? styles.revenueCard
                        : ""
                        }`}
                    >
                      <div className={styles.icon}>{item.icon}</div>
                      <div className={styles.amount}>
                        {item.key === "totalTipperAmt"
                          ? `$${item.amount?.toLocaleString("en-US", {
                            // style: "currency",
                            // currency: "USD",
                          })}`
                          : item.key === "average_tip_amount"
                            ? item?.avg_amount?.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                            : item.key === "star"
                              ? Number(item.amount || 0).toFixed(1)
                              : item.amount?.toLocaleString("en-US")}
                      </div>
                      <div className={styles.title}>{item.title}</div>
                    </div>
                  </div>
                ))}
            </div>
          )}
          {countResponse?.data?.properties > 0 && <ReactTable
            headings={tableHeadings}
            data={dashbaordData || []}
            actionColumn
            statusColumn={false}
            actionButtonOptions={actionButtonOptions}
            currentPage={currentPage}
            itemPerPage={pageLimit}
            totalItems={totalRecords}
            pageLimit={pageLimit}
            setPerPageLimit={setPageLimit}
            setCurrentPage={setCurrentPage}
            pagination={true}
            loading={!dashbaordData || dashBoardDataLoading}
            disableActionsForEmptyPropertyName={true}
          >
            <div
              className={styles.dashboardHeadings}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 10px",
              }}
            >
              <h2>Property Tips</h2>
              <div className={styles.search}>
                <div className={styles.searchField}>
                  <InputField
                    name="search_organization_name"
                    icon={searchIcon}
                    placeholder="Search by Property Name"
                    value={searchValue}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setSearchValue(e.target.value);
                    }}
                    autoComplete="off"
                  />
                  {searchValue ? (
                    <span
                      className={styles.crossIcon}
                      onClick={() => setSearchValue("")}
                    >
                      <RxCross1 color="#2549E8" />
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </ReactTable>}

        </Container>
      </div>
      <TippingModal
        show={showTippingModal}
        handleToggle={closeTippingModal}
        onStartJourney={startTippingJourney}
      />
      <RatingsPromptModal
        show={showRatingsPrompt}
        onAddTipping={addTipping}
        onKeepRatingsOnly={closeTippingModal}
        addTippingLoading={enableSimpleTippingLoading}
      />
    </DashboardLayout>
  );
};
export default Dashboard;
